export const VIDEO_FORMATS = new Set(['mp4', 'webm', 'mov']);
export const IMAGE_FORMATS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg']);
// Office formats rendered via the Microsoft Office Online embed viewer.
export const OFFICE_FORMATS = new Set(['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx']);
export const PREVIEWABLE_FORMATS = new Set([
  ...IMAGE_FORMATS,
  ...VIDEO_FORMATS,
  ...OFFICE_FORMATS,
  'pdf',
]);

export function isVideoFormat(fmt: string | null | undefined): boolean {
  return !!fmt && VIDEO_FORMATS.has(fmt.toLowerCase());
}

export function isImageFormat(fmt: string | null | undefined): boolean {
  return !!fmt && IMAGE_FORMATS.has(fmt.toLowerCase());
}

export function isPdfFormat(fmt: string | null | undefined): boolean {
  return fmt?.toLowerCase() === 'pdf';
}

export function isOfficeFormat(fmt: string | null | undefined): boolean {
  return !!fmt && OFFICE_FORMATS.has(fmt.toLowerCase());
}

// The Office Online viewer fetches the file server-side, so `fileUrl` must be a
// publicly reachable URL (our presigned S3 URLs qualify while they are valid).
export function buildOfficeViewerUrl(fileUrl: string): string {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
}

// Whether a resource may be previewed via the Office Online viewer. Compliance
// resources are excluded because the viewer fetches the (publicly reachable)
// file server-side, which would expose the document to a third party.
export function isOfficeViewerAllowed(
  fmt: string | null | undefined,
  resourceType?: string | null,
): boolean {
  return isOfficeFormat(fmt) && resourceType !== 'COMPLIANCE';
}

export function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getVideoMimeType(fmt: string | null | undefined): string {
  const map: Record<string, string> = {
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
  };
  return (fmt && map[fmt.toLowerCase()]) || 'video/mp4';
}

export function buildShareUrl(shareToken: string): string {
  return `${window.location.origin}/share/${shareToken}`;
}

export const RESOURCE_TYPE_LABELS: Record<string, string> = {
  FLYER: 'Flyer',
  COMPLIANCE: 'Compliance',
  TEMPLATE: 'Template',
  PRESENTATION: 'Presentation',
  VIDEO: 'Video',
  GUIDE: 'Guide',
  FORM: 'Form',
  OTHER: 'Other',
};
